import { decodeBase45, encodeBase45 } from './base45.ts';
import { blocksForSeed, sampleDegree } from './lt.ts';

/**
 * NetID stream frames (SPEC.md §4).
 *
 * Frame bytes (big-endian):
 *   ver u8 = 1 | stream u32 | size u32 | blockSize u16 | seed u32 | degree u16 | symbol[blockSize]
 *   ver u8 = 2 | … the same … | symbol[blockSize] | tick[82]       (v3 streams: a signed tick, tick.ts)
 * QR text: "NID1:" + Base45(frame bytes), alphanumeric mode.
 */
export const FRAME_VERSION = 1;
export const FRAME_VERSION_TICK = 2;
export const FRAME_TICK_BYTES = 82;
export const HEADER_BYTES = 17;
export const QR_PREFIX = 'NID1:';
export const MAX_PACKET_BYTES = 256 * 1024;

export type FrameHeader = { stream: number; size: number; blockSize: number; seed: number; degree: number };
export type Frame = FrameHeader & { symbol: Uint8Array; /** v2 frames: the raw signed tick (82 bytes, tick.ts). */ tick?: Uint8Array };

export function blockCount(size: number, blockSize: number): number {
  return Math.ceil(size / blockSize);
}

export function encodeFrame(f: Frame): Uint8Array {
  if (f.tick && f.tick.length !== FRAME_TICK_BYTES) throw new Error('a tick is 82 bytes');
  const out = new Uint8Array(HEADER_BYTES + f.blockSize + (f.tick ? FRAME_TICK_BYTES : 0));
  const dv = new DataView(out.buffer);
  dv.setUint8(0, f.tick ? FRAME_VERSION_TICK : FRAME_VERSION);
  dv.setUint32(1, f.stream >>> 0);
  dv.setUint32(5, f.size >>> 0);
  dv.setUint16(9, f.blockSize);
  dv.setUint32(11, f.seed >>> 0);
  dv.setUint16(15, f.degree);
  out.set(f.symbol, HEADER_BYTES);
  if (f.tick) out.set(f.tick, HEADER_BYTES + f.blockSize);
  return out;
}

export function decodeFrame(b: Uint8Array): Frame {
  if (b.length < HEADER_BYTES + 1) throw new Error('frame too short');
  const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
  const ver = dv.getUint8(0);
  if (ver !== FRAME_VERSION && ver !== FRAME_VERSION_TICK) throw new Error(`unknown frame version ${ver}`);
  const blockSize = dv.getUint16(9);
  const tick = ver === FRAME_VERSION_TICK;
  if (tick && b.length !== HEADER_BYTES + blockSize + FRAME_TICK_BYTES) throw new Error('bad block size');
  const f: Frame = {
    stream: dv.getUint32(1),
    size: dv.getUint32(5),
    blockSize,
    seed: dv.getUint32(11),
    degree: dv.getUint16(15),
    symbol: b.subarray(HEADER_BYTES, tick ? HEADER_BYTES + blockSize : b.length),
    ...(tick ? { tick: b.subarray(HEADER_BYTES + blockSize) } : {}),
  };
  if (f.size < 1 || f.size > MAX_PACKET_BYTES) throw new Error('bad packet size');
  if (f.blockSize < 1 || f.symbol.length !== f.blockSize) throw new Error('bad block size');
  const k = blockCount(f.size, f.blockSize);
  if (f.degree < 1 || f.degree > k) throw new Error('bad degree');
  return f;
}

export const frameToText = (b: Uint8Array): string => QR_PREFIX + encodeBase45(b);

export function textToFrame(text: string): Frame | null {
  if (!text.startsWith(QR_PREFIX)) return null;
  try {
    return decodeFrame(decodeBase45(text.slice(QR_PREFIX.length)));
  } catch {
    return null;
  }
}

/** Rateless encoder for one packet. */
export class FountainEncoder {
  readonly packet: Uint8Array;
  readonly blockSize: number;
  readonly stream: number;
  readonly k: number;
  readonly blocks: Uint8Array[];
  private nextSeed: number;

  constructor(packet: Uint8Array, blockSize = 160, stream = randomU32()) {
    this.packet = packet;
    this.blockSize = blockSize;
    this.stream = stream >>> 0;
    if (packet.length < 1 || packet.length > MAX_PACKET_BYTES) throw new Error('packet size out of range');
    this.k = blockCount(packet.length, blockSize);
    this.blocks = [];
    for (let i = 0; i < this.k; i++) {
      const block = new Uint8Array(blockSize); // zero-padded tail
      block.set(packet.subarray(i * blockSize, (i + 1) * blockSize));
      this.blocks.push(block);
    }
    this.nextSeed = randomU32();
  }

  frame(seed: number): Frame {
    const degree = sampleDegree(seed, this.k);
    const symbol = new Uint8Array(this.blockSize);
    for (const i of blocksForSeed(seed, this.k, degree)) xorInto(symbol, this.blocks[i]);
    return { stream: this.stream, size: this.packet.length, blockSize: this.blockSize, seed: seed >>> 0, degree, symbol };
  }

  /** The next frame, as QR text; with `tick` (82 bytes) a v2 frame that carries it. */
  nextText(tick?: Uint8Array): string {
    const seed = this.nextSeed;
    this.nextSeed = (this.nextSeed + 1) >>> 0;
    return frameToText(encodeFrame({ ...this.frame(seed), ...(tick ? { tick } : {}) }));
  }
}

/**
 * Decoder for one stream: incremental Gaussian elimination over GF(2), so
 * any k linearly independent frames rebuild the packet (typically k + 2..5
 * frames; order, loss and repeats don't matter).
 */
export class FountainDecoder {
  readonly k: number;
  private readonly words: number;
  /** pivot column -> [coefficient bitset, symbol]; a row's lowest set bit is its pivot. */
  private readonly rows = new Map<number, { mask: Uint32Array; data: Uint8Array }>();
  private readonly seen = new Set<number>();
  frames = 0;
  private result: Uint8Array | null = null;

  readonly stream: number;
  readonly size: number;
  readonly blockSize: number;

  constructor(stream: number, size: number, blockSize: number) {
    this.stream = stream;
    this.size = size;
    this.blockSize = blockSize;
    this.k = blockCount(size, blockSize);
    this.words = Math.ceil(this.k / 32);
  }

  get rank(): number {
    return this.rows.size;
  }

  get progress(): number {
    return this.rows.size / this.k;
  }

  get done(): boolean {
    return this.result !== null;
  }

  /** Adds a frame of this stream; returns the packet once it is complete. */
  add(f: Frame): Uint8Array | null {
    if (this.result) return this.result;
    if (f.stream !== this.stream || f.size !== this.size || f.blockSize !== this.blockSize) return null;
    const key = f.seed * 65536 + f.degree;
    if (this.seen.has(key)) return null;
    this.seen.add(key);
    this.frames++;
    const mask = new Uint32Array(this.words);
    for (const i of blocksForSeed(f.seed, this.k, f.degree)) mask[i >>> 5] |= 1 << (i & 31);
    const data = Uint8Array.from(f.symbol);
    // Reduce against existing pivots, lowest column first (a pivot row only has bits above its pivot).
    for (let col = lowestBit(mask, 0); col >= 0; col = lowestBit(mask, col + 1)) {
      const pivot = this.rows.get(col);
      if (!pivot) {
        this.rows.set(col, { mask, data });
        if (this.rows.size === this.k) this.result = this.solve();
        return this.result;
      }
      xorWords(mask, pivot.mask);
      xorInto(data, pivot.data);
    }
    return null; // linearly dependent: nothing new
  }

  private solve(): Uint8Array {
    const solved: Uint8Array[] = new Array(this.k);
    for (let col = this.k - 1; col >= 0; col--) {
      const row = this.rows.get(col)!;
      const data = Uint8Array.from(row.data);
      for (let c = lowestBit(row.mask, col + 1); c >= 0; c = lowestBit(row.mask, c + 1)) xorInto(data, solved[c]);
      solved[col] = data;
    }
    const out = new Uint8Array(this.size);
    for (let i = 0; i < this.k; i++) out.set(solved[i].subarray(0, Math.min(this.blockSize, this.size - i * this.blockSize)), i * this.blockSize);
    return out;
  }
}

/**
 * What a scanner feeds every QR text into: one decoder per stream (the
 * player switches streams whenever the server signs a fresh packet), the
 * first complete packet wins.
 */
export class StreamCollector {
  private readonly decoders = new Map<number, FountainDecoder>();
  current: FountainDecoder | null = null;
  /** The last frame read (its tick, if any, is what the scanner's tick rule needs). */
  lastFrame: Frame | null = null;
  rejected = 0;

  private readonly maxStreams: number;

  constructor(maxStreams = 4) {
    this.maxStreams = maxStreams;
  }

  addText(text: string): Uint8Array | null {
    const f = textToFrame(text);
    if (!f) {
      this.rejected++;
      this.lastFrame = null;
      return null;
    }
    this.lastFrame = f;
    return this.addFrame(f);
  }

  addFrame(f: Frame): Uint8Array | null {
    let d = this.decoders.get(f.stream);
    if (!d) {
      d = new FountainDecoder(f.stream, f.size, f.blockSize);
      this.decoders.set(f.stream, d);
      while (this.decoders.size > this.maxStreams) this.decoders.delete(this.decoders.keys().next().value!);
    }
    this.current = d;
    return d.add(f);
  }

  reset(): void {
    this.decoders.clear();
    this.current = null;
    this.lastFrame = null;
    this.rejected = 0;
  }
}

function lowestBit(mask: Uint32Array, from: number): number {
  for (let w = from >>> 5; w < mask.length; w++) {
    let word = mask[w];
    if (w === from >>> 5) word &= ~0 << (from & 31);
    if (word) return (w << 5) + (31 - Math.clz32(word & -word));
  }
  return -1;
}

function xorWords(a: Uint32Array, b: Uint32Array): void {
  for (let i = 0; i < a.length; i++) a[i] ^= b[i];
}

function xorInto(a: Uint8Array, b: Uint8Array): void {
  for (let i = 0; i < a.length; i++) a[i] ^= b[i];
}

function randomU32(): number {
  const b = new Uint32Array(1);
  crypto.getRandomValues(b);
  return b[0];
}
